import React, { useState } from 'react';
import {
	Alert,
	Button,
	Tooltip,
	Stack,
	Typography,
	Grid,
	TextField,
} from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import DateFnsUtils from '@date-io/date-fns';
import { DataGrid, GridToolbar, esES } from '@mui/x-data-grid';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import moment from 'moment';
import { getResults, getResultsExcel } from '../../actions/biases';
import { canUserSeeMoney, money, whichTypeOfClient } from '../../helpers/utils';
import { formatDateMX } from '../../helpers/hours';
import { useSelector } from 'react-redux';
import { LoadingButton } from '@mui/lab';

// Results table
export const SummaryResults = () => {
	// Date state
	const [date1, setDate1] = useState(null);
	const [date2, setDate2] = useState();

	// Page size
	const [pageSize, setPageSize] = useState(20);

	// Total biases
	const [resultsTotal, setResultsTotal] = useState(-1);

	// Is loading
	const [isLoading, setIsLoading] = useState(false);
	const [isLoadingDownload, setIsLoadingDownload] = useState(false);

	// User
	const { user } = useSelector((state) => state);

	// Rows state
	const [rows, setRows] = useState(null);

	// Generate rows
	const generateRows = async (formattedDate1, formattedDate2) => {
		setIsLoading(true);
		// Get results by date from API
		const { data } = await getResults(formattedDate1, formattedDate2);

		// If results are empty set an empty array
		if (data?.results?.length === 0 || !data?.ok) {
			setRows([]);
		} else {
			// Set count
			setResultsTotal(data.count);

			// Map data results
			const res = data?.results.map((item) => ({
				...item,
				division: item.division,
				number_order: item.number_order,
				entry_date: formatDateMX(item.entry_date),
				delivery_date: formatDateMX(item.delivery_date),
				delivery_forecast: formatDateMX(item.delivery_forecast),
				authorization_date: formatDateMX(item.authorization_date),
				validation_date: formatDateMX(item.validation_date),
				released_date: formatDateMX(item.released_date),
				bias_key: item.bias,
				created_at: formatDateMX(item.created_at),
				company_name: item.company_name,
				state: item.state,
				municipality: item.municipality || 'No asignado',
				type: item?.type ?? '-',
				type_of_client: item?.type_of_client ?? 'No definido',
				units: item.units,
				created_by: item?.created_by || 'No definido',
				variant: item?.variant || 'No asignado',
				variant_style: item?.variant_style || 'No asignado',
				unit_price: canUserSeeMoney(user)
					? money(item.unit_price)
					: '$ ----',
				current_process: item?.current_process || 'No asignado',
				current_process_date: item?.current_process_date,
				next_process: item?.next_process || 'No asignado',
				status: item?.status,
			}));

			// Set mapped rows
			setRows(res);
		}

		setIsLoading(false);
	};

	// Handle get results
	const handleGetResults = () => {
		// Date format to YYYY-MM-DD
		const formattedDate1 = moment(date1).format('YYYY-MM-DD');
		const formattedDate2 = moment(date2).format('YYYY-MM-DD');
		// Generate rows from formatted date
		generateRows(formattedDate1, formattedDate2);
	};

	// Handle get results
	const handleGetResultsExcel = () => {
		setIsLoadingDownload(true);
		setTimeout(() => {
			// Date format to YYYY-MM-DD
			const formattedDate1 = moment(date1).format('YYYY-MM-DD');
			const formattedDate2 = moment(date2).format('YYYY-MM-DD');
			// Generate rows from formatted date
			getResultsExcel(formattedDate1, formattedDate2);
			setIsLoadingDownload(false);
		}, 1200);
	};

	// Handle Reset
	const handleReset = () => {
		setDate1(null);
		setDate2(null);
		setRows([]);
		setResultsTotal(-1);
		setIsLoading(false);
	};

	// Columns
	const columns = [
		{
			field: 'division',
			headerName: 'División',
			...columnStyleCell,
			width: 100,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<span className='table-cell-trucate'>{row?.division}</span>
				</div>
			),
		},
		{
			field: 'created_by',
			headerName: 'Creado por',
			...columnStyleCell,
			width: 200,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<span className='table-cell-trucate'>
						{row?.created_by}
					</span>
				</div>
			),
		},
		{
			field: 'number_order',
			headerName: 'Pedido',
			...columnStyleCell,
			width: 100,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<span className='table-cell-trucate'>
						{row?.number_order}
					</span>
				</div>
			),
		},
		{
			field: 'entry_date',
			headerName: 'Fecha de pedido',
			...columnStyleCell,
			width: 150,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<span className='table-cell-trucate'>
						{row?.entry_date}
					</span>
				</div>
			),
		},
		{
			field: 'delivery_date',
			headerName: 'Fecha de entrega',
			...columnStyleCell,
			width: 150,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<span className='table-cell-trucate'>
						{row?.delivery_date}
					</span>
				</div>
			),
		},
		{
			field: 'delivery_forecast',
			headerName: 'Fecha estimada',
			...columnStyleCell,
			width: 150,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<span className='table-cell-trucate'>
						{row?.delivery_forecast}
					</span>
				</div>
			),
		},
		{
			field: 'authorization_date',
			headerName: 'Fecha de autorización',
			...columnStyleCell,
			width: 150,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<span className='table-cell-trucate'>
						{row?.authorization_date}
					</span>
				</div>
			),
		},
		{
			field: 'validation_date',
			headerName: 'Fecha de validación',
			...columnStyleCell,
			width: 150,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<span className='table-cell-trucate'>
						{row?.validation_date}
					</span>
				</div>
			),
		},
		{
			field: 'released_date',
			headerName: 'Fecha de liberación',
			...columnStyleCell,
			width: 150,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<span className='table-cell-trucate'>
						{row?.released_date}
					</span>
				</div>
			),
		},
		{
			field: 'bias_key',
			headerName: 'Partida',
			...columnStyleCell,
			width: 150,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<span className='table-cell-trucate'>{row?.bias_key}</span>
				</div>
			),
		},
		{
			field: 'created_at',
			headerName: 'Fecha de partida',
			...columnStyleCell,
			width: 150,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<span className='table-cell-trucate'>
						{row?.created_at}
					</span>
				</div>
			),
		},
		{
			field: 'company_name',
			headerName: 'Cliente',
			...columnStyleCell,
			width: 200,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<Tooltip title={row?.company_name}>
						<span className='table-cell-trucate'>
							{row?.company_name}
						</span>
					</Tooltip>
				</div>
			),
		},
		{
			field: 'state',
			headerName: 'Estado',
			...columnStyleCell,
			width: 200,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<Tooltip title={row?.state}>
						<span className='table-cell-trucate'>{row?.state}</span>
					</Tooltip>
				</div>
			),
		},
		{
			field: 'municipality',
			headerName: 'Municipio',
			...columnStyleCell,
			width: 200,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<Tooltip title={row?.municipality}>
						<span className='table-cell-trucate'>
							{row?.municipality}
						</span>
					</Tooltip>
				</div>
			),
		},
		{
			field: 'type',
			headerName: 'Tipo de cliente (original)',
			...columnStyleCell,
			width: 200,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<Tooltip title={row?.type}>
						<span className='table-cell-trucate'>{row?.type}</span>
					</Tooltip>
				</div>
			),
		},
		{
			field: 'type_of_client',
			headerName: 'Tipo de cliente (pedido)',
			...columnStyleCell,
			width: 200,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<Tooltip title={row?.type_of_client}>
						<span className='table-cell-trucate'>
							{whichTypeOfClient(row?.type_of_client)?.toUpperCase()}
						</span>
					</Tooltip>
				</div>
			),
		},
		{
			field: 'units',
			headerName: 'Unidades',
			...columnStyleCell,
			width: 130,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<span className='table-cell-trucate'>{row?.units}</span>
				</div>
			),
		},
		{
			field: 'unit_price',
			headerName: 'Precio unitario',
			...columnStyleCell,
			width: 130,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<span className='table-cell-trucate'>
						{row?.unit_price}
					</span>
				</div>
			),
		},
		{
			field: 'garment',
			headerName: 'Prenda',
			...columnStyleCell,
			width: 150,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<span className='table-cell-trucate'>
						{row?.garment?.name}
					</span>
				</div>
			),
		},
		{
			field: 'variant',
			headerName: 'Variante',
			...columnStyleCell,
			width: 150,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<span className='table-cell-trucate'>{row?.variant}</span>
				</div>
			),
		},
		{
			field: 'variant_style',
			headerName: 'Estilo',
			...columnStyleCell,
			width: 150,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<span className='table-cell-trucate'>
						{row?.variant_style}
					</span>
				</div>
			),
		},
		{
			field: 'fabric',
			headerName: 'Tela',
			...columnStyleCell,
			width: 200,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<Tooltip title={row?.fabric?.name}>
						<span className='table-cell-trucate'>
							{row?.fabric?.name}
						</span>
					</Tooltip>
				</div>
			),
		},
		{
			field: 'color',
			headerName: 'Color',
			...columnStyleCell,
			width: 150,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<span className='table-cell-trucate'>
						{row?.fabric?.color}
					</span>
				</div>
			),
		},
		{
			field: 'current_process',
			headerName: 'Proceso actual',
			...columnStyleCell,
			width: 150,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<span className='table-cell-trucate'>
						{row?.current_process}
					</span>
				</div>
			),
		},
		{
			field: 'current_process_date',
			headerName: 'Fecha de proceso',
			...columnStyleCell,
			width: 150,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<span className='table-cell-trucate'>
						{row?.current_process_date}
					</span>
				</div>
			),
		},
		{
			field: 'next_process',
			headerName: 'Proceso siguiente',
			...columnStyleCell,
			width: 150,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<span className='table-cell-trucate'>
						{row?.next_process}
					</span>
				</div>
			),
		},
		{
			field: 'status',
			headerName: 'Estatus de partida',
			...columnStyleCell,
			width: 150,
			renderCell: ({ row }) => (
				<div style={rowDialogStyle}>
					<span
						className='table-cell-trucate'
						style={{
							color:
								row?.status === 'RETRASADO' ? 'red' : 'green',
						}}
					>
						{row?.status}
					</span>
				</div>
			),
		},
	];

	return (
		<div style={divStyle}>
			<Stack
				direction='row'
				alignItems='flex-end'
				justifyContent='space-between'
				style={stackStyle}
			>
				<Typography variant='h4' gutterBottom>
					Resumen de pedidos
				</Typography>
				<LocalizationProvider
					dateAdapter={AdapterDateFns}
					adapterLocale={es}
					utils={DateFnsUtils}
				>
					<Grid container spacing={2} style={gridStyle}>
						<Grid item xs={3}>
							{/* Date 1 */}
							<DesktopDatePicker
								style={{ width: '50%' }}
								label='Desde'
								inputFormat='dd/MM/yyyy'
								value={date1}
								onChange={(val) => {
									setDate1(val);
								}}
								renderInput={(params) =>
									SelectComponent(params, 'Desde')
								}
							/>
						</Grid>
						<Grid item xs={3}>
							{/* Date 2 */}
							<DesktopDatePicker
								style={{ width: '50%' }}
								label='Desde'
								inputFormat='dd/MM/yyyy'
								value={date2}
								onChange={(val) => {
									setDate2(val);
								}}
								renderInput={(params) =>
									SelectComponent(params, 'Hasta')
								}
							/>
						</Grid>
						<Grid
							item
							xs={date1 !== null && date2 !== null ? 5 : 2}
							style={gridButtonStyle}
						>
							{date1 !== null && date2 !== null && (
								<Button
									variant='contained'
									color='error'
									onClick={handleReset}
									style={{ margin: '0 15px 0 0' }}
								>
									Limpiar
								</Button>
							)}
							<LoadingButton
								variant='contained'
								color='primary'
								onClick={handleGetResults}
								loading={isLoading}
								style={{ margin: '0 15px 0 0' }}
								disabled={date1 === null || date2 === null}
							>
								Obtener
							</LoadingButton>
							{rows?.length > 0 && (
								<LoadingButton
									variant='contained'
									color='success'
									onClick={handleGetResultsExcel}
									loading={isLoadingDownload}
									disabled={date1 === null || date2 === null}
								>
									Exportar
								</LoadingButton>
							)}
						</Grid>
					</Grid>
				</LocalizationProvider>
			</Stack>
			<hr />

			{resultsTotal >= 0 && (
				<Typography variant='h6'>
					Total de partidas: {resultsTotal}
				</Typography>
			)}

			<div style={datagridTableStyle}>
				{/* Datagrid table */}
				{rows?.length > 0 ? (
					<DataGrid
						autoHeight
						loading={isLoading}
						rowsPerPageOptions={[20, 30, 50, 70, 100]}
						pageSize={pageSize}
						onPageSizeChange={(newPageSize) =>
							setPageSize(newPageSize)
						}
						density='compact'
						rows={rows}
						getRowHeight={() => 'auto'}
						columns={columns}
						components={{ Toolbar: GridToolbar }}
						localeText={
							esES.components.MuiDataGrid.defaultProps.localeText
						}
					/>
				) : (
					<>
						{rows === null ? (
							<Alert severity='success'>
								Busca todas las partidas de un periodo de tiempo
							</Alert>
						) : (
							<Alert severity='info'>
								No hay ninguna partida
							</Alert>
						)}
					</>
				)}
			</div>
		</div>
	);
};

export const SelectComponent = (params, label) => (
	<TextField fullWidth required margin='normal' {...params} label={label} />
);

export const rowDialogStyle = {
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	textAlign: 'center',
	justifyContent: 'space-between',
	margin: '0 auto',
};

export const stackStyle = {
	padding: '20px 20px 0 0',
	margin: '0 0 10px 0',
	width: '100%',
};

export const divStyle = {
	padding: '20px',
};

export const gridStyle = {
	width: '70%',
	display: 'flex',
	justifyContent: 'flex-end',
};

export const gridButtonStyle = {
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
};

export const datagridTableStyle = { margin: '20px 0' };

export const columnStyleCell = {
	headerClassName: 'column-header-pilu',
	headerAlign: 'center',
};
